/**
 * Schema.org structured data generators for SEO
 * Used for schema stacking across all pages
 */

const BUSINESS = {
  name: "Overhaul Ops LLC",
  url: "https://overhaulops.com",
  logo: "https://overhaulops.com/logo192.png",
  phone: "+1-817-682-1555",
  email: "overhaulops.llc@gmail.com",
  city: "Fort Worth",
  state: "TX",
  country: "US",
  lat: "32.7555",
  lng: "-97.3308",
  priceRange: "$$",
  hours: "Mo-Su 07:00-18:00",
  facebook: "https://www.facebook.com/share/17Bi5mnS1q/",
};

const COUNTIES = [
  { name: "Denton County" },
  { name: "Tarrant County" },
  { name: "Parker County" },
  { name: "Wise County" },
];

/** Base LocalBusiness — included on every page */
export function localBusinessSchema(overrides = {}) {
  return {
    "@type": "LocalBusiness",
    "@id": `${BUSINESS.url}/#localbusiness`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    logo: BUSINESS.logo,
    image: overrides.image || BUSINESS.logo,
    telephone: overrides.phone || BUSINESS.phone,
    email: overrides.email || BUSINESS.email,
    address: {
      "@type": "PostalAddress",
      addressLocality: BUSINESS.city,
      addressRegion: BUSINESS.state,
      addressCountry: BUSINESS.country,
    },
    geo: {
      "@type": "GeoCoordinates",
      latitude: BUSINESS.lat,
      longitude: BUSINESS.lng,
    },
    areaServed: COUNTIES.map(c => ({ "@type": "AdministrativeArea", name: c.name })),
    openingHoursSpecification: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "07:00",
      closes: "18:00",
    },
    priceRange: BUSINESS.priceRange,
    sameAs: [BUSINESS.facebook],
    ...overrides,
  };
}

/** BreadcrumbList — included on every page */
export function breadcrumbSchema(crumbs) {
  return {
    "@type": "BreadcrumbList",
    itemListElement: crumbs.map((crumb, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: crumb.name,
      item: crumb.url ? `${BUSINESS.url}${crumb.url}` : undefined,
    })),
  };
}

/** WebSite — Home page */
export function websiteSchema() {
  return {
    "@type": "WebSite",
    "@id": `${BUSINESS.url}/#website`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    publisher: { "@id": `${BUSINESS.url}/#organization` },
  };
}

/** Organization — Home & About */
export function organizationSchema() {
  return {
    "@type": "Organization",
    "@id": `${BUSINESS.url}/#organization`,
    name: BUSINESS.name,
    url: BUSINESS.url,
    logo: {
      "@type": "ImageObject",
      url: BUSINESS.logo,
    },
    contactPoint: {
      "@type": "ContactPoint",
      telephone: BUSINESS.phone,
      contactType: "customer service",
      areaServed: "US",
      availableLanguage: "English",
    },
    sameAs: [BUSINESS.facebook],
  };
}

/** Service schema — for service categories */
export function serviceSchema(service) {
  return {
    "@type": "Service",
    name: service.name,
    description: service.description || "",
    provider: { "@id": `${BUSINESS.url}/#localbusiness` },
    areaServed: COUNTIES.map(c => ({ "@type": "AdministrativeArea", name: c.name })),
    ...(service.image ? { image: service.image } : {}),
  };
}

/** Offer schema — for pricing */
export function offerSchema(description) {
  return {
    "@type": "Offer",
    description: description || "Free, no-obligation quote",
    price: "0",
    priceCurrency: "USD",
    availability: "https://schema.org/InStock",
    seller: { "@id": `${BUSINESS.url}/#localbusiness` },
  };
}

/** AggregateRating — Testimonials */
export function aggregateRatingSchema(count, rating) {
  return {
    "@type": "AggregateRating",
    itemReviewed: {
      "@type": "LocalBusiness",
      "@id": `${BUSINESS.url}/#localbusiness`,
      name: BUSINESS.name,
      image: BUSINESS.logo,
    },
    ratingValue: String(rating || "5.0"),
    reviewCount: String(count || "1"),
    bestRating: "5",
    worstRating: "1",
  };
}

/** Review schema — individual review */
export function reviewSchema(review) {
  return {
    "@type": "Review",
    itemReviewed: {
      "@type": "LocalBusiness",
      "@id": `${BUSINESS.url}/#localbusiness`,
      name: BUSINESS.name,
      image: BUSINESS.logo,
    },
    author: { "@type": "Person", name: review.name || "Customer" },
    reviewRating: {
      "@type": "Rating",
      ratingValue: String(review.rating || 5),
      bestRating: "5",
      worstRating: "1",
    },
    reviewBody: review.text || "",
    datePublished: review.date || new Date().toISOString().split("T")[0],
  };
}

/** Article / BlogPosting */
export function articleSchema(post) {
  return {
    "@type": "BlogPosting",
    headline: post.title || "",
    description: post.description || "",
    image: post.image || BUSINESS.logo,
    author: { "@type": "Organization", name: BUSINESS.name },
    publisher: {
      "@type": "Organization",
      name: BUSINESS.name,
      logo: { "@type": "ImageObject", url: BUSINESS.logo },
    },
    datePublished: post.date || new Date().toISOString().split("T")[0],
    dateModified: post.modified || post.date || new Date().toISOString().split("T")[0],
    mainEntityOfPage: post.url || BUSINESS.url,
  };
}

/** FAQPage — for services page */
export function faqSchema(questions) {
  return {
    "@type": "FAQPage",
    mainEntity: questions.map(q => ({
      "@type": "Question",
      name: q.question,
      acceptedAnswer: {
        "@type": "Answer",
        text: q.answer,
      },
    })),
  };
}

/** ImageGallery */
export function imageGallerySchema(images) {
  return {
    "@type": "ImageGallery",
    name: `${BUSINESS.name} Project Gallery`,
    description: "Photos of completed junk removal, cleanout, and hauling projects by Overhaul Ops LLC",
    image: images.map(img => ({
      "@type": "ImageObject",
      url: img.url || img,
      name: img.alt || img.caption || "Project photo",
      description: img.caption || "Junk removal project in DFW",
    })),
  };
}

/** ContactPoint — Contact page */
export function contactPointSchema() {
  return {
    "@type": "ContactPoint",
    telephone: BUSINESS.phone,
    email: BUSINESS.email,
    contactType: "customer service",
    areaServed: "US",
    availableLanguage: "English",
    hoursAvailable: {
      "@type": "OpeningHoursSpecification",
      dayOfWeek: ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"],
      opens: "07:00",
      closes: "18:00",
    },
  };
}

/**
 * Build the full @graph array for JSON-LD injection.
 * Always includes LocalBusiness + BreadcrumbList, plus page-specific schemas.
 */
export function buildSchemaGraph(pageSchemas = [], breadcrumbs = [], overrides = {}) {
  const graph = [
    localBusinessSchema(overrides),
  ];

  if (breadcrumbs.length > 0) {
    graph.push(breadcrumbSchema(breadcrumbs));
  }

  graph.push(...pageSchemas);

  return {
    "@context": "https://schema.org",
    "@graph": graph,
  };
}
